import { useMutation } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { licenceAPI } from '../../api/index'
import { queryClient } from '../../App'
import { Licence } from '../../types/index'
import { Button } from '../Button'

interface AddLicenceModalProps {
  open: boolean
  onClose: () => void
  customerId: string
  licence?: Licence
  onSuccess?: () => void
}

type LicenceType = 'learner' | 'permanent' | 'commercial'

const licenceTypes: LicenceType[] = ['learner', 'permanent', 'commercial']

export default function AddLicenceModal({
  open,
  onClose,
  customerId,
  licence,
  onSuccess,
}: AddLicenceModalProps) {
  const [formData, setFormData] = useState({
    licence_number: '',
    licence_type: '' as LicenceType | '',
    vehicle_classes: [] as string[],
    issue_date: '',
    expiry_date: '',
    renewal_date: '',
    issuing_rto: '',
    blood_group: '',
  })

  const [classInput, setClassInput] = useState('')

  useEffect(() => {
    if (licence) {
      setFormData({
        licence_number: licence.licence_number || '',
        licence_type: licence.licence_type || '',
        vehicle_classes: licence.vehicle_classes || [],
        issue_date: licence.issue_date || '',
        expiry_date: licence.expiry_date || '',
        renewal_date: licence.renewal_date || '',
        issuing_rto: licence.issuing_rto || '',
        blood_group: licence.blood_group || '',
      })
    } else {
      setFormData({
        licence_number: '',
        licence_type: '',
        vehicle_classes: [],
        issue_date: '',
        expiry_date: '',
        renewal_date: '',
        issuing_rto: '',
        blood_group: '',
      })
    }
    setClassInput('')
  }, [licence, open])

  const handleAddClass = () => {
    if (classInput.trim()) {
      setFormData({
        ...formData,
        vehicle_classes: [...formData.vehicle_classes, classInput.trim()],
      })
      setClassInput('')
    }
  }

  const handleRemoveClass = (index: number) => {
    setFormData({
      ...formData,
      vehicle_classes: formData.vehicle_classes.filter((_, i) => i !== index),
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      handleAddClass()
    }
  }

  const createMutation = useMutation({
    mutationFn: () =>
      licenceAPI.create(customerId, {
        ...formData,
        licence_type: formData.licence_type || null,
      }),
    onSuccess: () => {
      toast.success('Licence created')
      queryClient.invalidateQueries({ queryKey: ['licences', customerId] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to create licence')
    },
  })

  const updateMutation = useMutation({
    mutationFn: () =>
      licenceAPI.update(licence!.id, {
        ...formData,
        licence_type: formData.licence_type || null,
      }),
    onSuccess: () => {
      toast.success('Licence updated')
      queryClient.invalidateQueries({ queryKey: ['licences', customerId] })
      queryClient.invalidateQueries({ queryKey: ['licence', licence!.id] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to update licence')
    },
  })

  const isLoading = createMutation.isPending || updateMutation.isPending
  const isEdit = !!licence

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.licence_number.trim()) {
      toast.error('Licence number is required')
      return
    }
    if (isEdit) {
      updateMutation.mutate()
    } else {
      createMutation.mutate()
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Licence' : 'Add Licence'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Licence Number *
              </label>
              <input
                type="text"
                required
                disabled={isLoading}
                value={formData.licence_number}
                onChange={(e) =>
                  setFormData({ ...formData, licence_number: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="DL-01-23-12345"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Licence Type
              </label>
              <select
                disabled={isLoading}
                value={formData.licence_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    licence_type: e.target.value as LicenceType | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select licence type</option>
                {licenceTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2 - Vehicle Classes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle Classes
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  disabled={isLoading}
                  value={classInput}
                  onChange={(e) => setClassInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter class (e.g., MC, LMV) and press Enter or comma"
                  className="flex-1 rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                />
                <button
                  type="button"
                  disabled={isLoading || !classInput.trim()}
                  onClick={handleAddClass}
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
              {formData.vehicle_classes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.vehicle_classes.map((vehicleClass, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-saffron/10 text-saffron text-sm"
                    >
                      {vehicleClass}
                      <button
                        type="button"
                        disabled={isLoading}
                        onClick={() => handleRemoveClass(index)}
                        className="hover:opacity-70 disabled:opacity-50"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Issue Date
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.issue_date}
                onChange={(e) =>
                  setFormData({ ...formData, issue_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Expiry Date
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.expiry_date}
                onChange={(e) =>
                  setFormData({ ...formData, expiry_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Renewal Date
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.renewal_date}
                onChange={(e) =>
                  setFormData({ ...formData, renewal_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Issuing RTO
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.issuing_rto}
                onChange={(e) =>
                  setFormData({ ...formData, issuing_rto: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Mumbai RTO"
              />
            </div>
          </div>

          {/* Row 5 */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Blood Group
            </label>
            <input
              type="text"
              disabled={isLoading}
              value={formData.blood_group}
              onChange={(e) =>
                setFormData({ ...formData, blood_group: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              placeholder="O+"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-4 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              variant="primary"
            >
              {isEdit ? 'Update Licence' : 'Add Licence'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
